import { Campaign } from "@/types/campaign";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Megaphone } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/EmptyState";

interface CampaignsTableProps {
  campaigns: Campaign[];
  counts?: Record<string, number>;
  onEdit?: (c: Campaign) => void;
  onAssign?: (c: Campaign) => void;
  onChangeStatus?: (id: string, status: Campaign["status"]) => void;
}

export function CampaignsTable({ campaigns, counts = {}, onEdit, onAssign, onChangeStatus }: CampaignsTableProps) {
  const isMobile = useIsMobile();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (isMobile) {
    if (campaigns.length === 0) {
      return <EmptyState icon={<Megaphone className="h-8 w-8 text-muted-foreground" />} title="No campaigns yet" description="Create your first campaign to get started." />;
    }
    return (
      <div className="space-y-3">
        {campaigns.map((campaign) => (
          <Card key={campaign.campaign_id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium truncate">{campaign.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {counts[campaign.campaign_id] ?? 0} numbers
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={getStatusColor(campaign.status)}>
                    {campaign.status}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAssign?.(campaign)}>
                        Manage Ads
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit?.(campaign)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onChangeStatus?.(campaign.campaign_id, 'active')}>
                        Set Active
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onChangeStatus?.(campaign.campaign_id, 'paused')}>
                        Set Paused
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onChangeStatus?.(campaign.campaign_id, 'archived')}>
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-3">
                  {campaign.utm_source && <span>{campaign.utm_source}</span>}
                  {campaign.utm_medium && <span>/ {campaign.utm_medium}</span>}
                </div>
                <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign Name</TableHead>
            <TableHead>Numbers</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Source</TableHead>
            <TableHead className="hidden md:table-cell">Medium</TableHead>
            <TableHead className="hidden lg:table-cell">Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map((campaign) => (
            <TableRow key={campaign.campaign_id}>
              <TableCell className="font-medium">{campaign.name}</TableCell>
              <TableCell>{counts[campaign.campaign_id] ?? 0}</TableCell>
              <TableCell>
                <Select value={campaign.status} onValueChange={(v: any) => onChangeStatus?.(campaign.campaign_id, v)}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="hidden md:table-cell">{campaign.utm_source || '-'}</TableCell>
              <TableCell className="hidden md:table-cell">{campaign.utm_medium || '-'}</TableCell>
              <TableCell className="hidden lg:table-cell">{new Date(campaign.created_at).toLocaleDateString()}</TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => onAssign?.(campaign)}>
                    Manage Ads
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => onEdit?.(campaign)}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
